/**
 * Static contract for the performance-agent Run modal: the option
 * lists, defaults, and limits that the form renders and that `buildVariables`
 * / `validate` enforce.
 */

import { DEFAULT_PROVIDER } from "../agentEngine/config";

import type { Provider } from "../agentEngine/config";

/**
 * Task-type discriminator. `base: "performance-agent"` expands to a
 * `performance-agent:<major>:<build>` image type, so the prefix is stable
 * across the user-chosen task NAME (which can collide with a customer task).
 */
export const PERFORMANCE_AGENT_TASK_TYPE_PREFIX = "performance-agent:";

export const isPerformanceAgentTask = (taskType: string | undefined): boolean =>
  Boolean(taskType?.startsWith(PERFORMANCE_AGENT_TASK_TYPE_PREFIX));

export type TimeUnit = "minutes" | "hours" | "days";

export const TIME_UNIT_SECONDS: Record<TimeUnit, number> = {
  minutes: 60,
  hours: 3600,
  days: 86400
};

export const TIME_UNITS: { value: TimeUnit; label: string }[] = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" }
];

export const DEFAULT_ANALYZE_PAST_VALUE = "15";
export const DEFAULT_ANALYZE_PAST_UNIT: TimeUnit = "minutes";

/** One timeframe chip. `value` narrows to a literal union via `as const` below. */
export interface AnalyzePresetOption {
  value: string;
  labelId: string;
  amount: number;
  unit: TimeUnit;
  /** Locked behind the continuous-profiling upgrade — rendered, not selectable. */
  requiresUpgrade: boolean;
}

/**
 * The fixed analysis windows, in chip render order. THE source of truth: the
 * `AnalyzePreset` union below is derived from it, so a preset can never exist
 * without a window — which is why `resolveWindowSeconds` needs no fallback.
 *
 * Only `15m` is free: without the continuous-profiling upgrade, profiling data
 * goes back 15 minutes, so a longer window would analyze data the project does
 * not have. That cap lives here and nowhere else — it is a table row, not a
 * validation branch.
 */
export const ANALYZE_PRESETS = [
  {
    value: "15m",
    labelId:
      "task_containers.run_modal.performance_agent.analyze_past.preset.15m",
    amount: 15,
    unit: "minutes",
    requiresUpgrade: false
  },
  {
    value: "1h",
    labelId:
      "task_containers.run_modal.performance_agent.analyze_past.preset.1h",
    amount: 1,
    unit: "hours",
    requiresUpgrade: true
  },
  {
    value: "24h",
    labelId:
      "task_containers.run_modal.performance_agent.analyze_past.preset.24h",
    amount: 24,
    unit: "hours",
    requiresUpgrade: true
  },
  {
    value: "7d",
    labelId:
      "task_containers.run_modal.performance_agent.analyze_past.preset.7d",
    amount: 7,
    unit: "days",
    requiresUpgrade: true
  }
] as const satisfies readonly AnalyzePresetOption[];

/** A fixed window. Derived, so it always matches `ANALYZE_PRESETS`. */
export type AnalyzePresetValue = (typeof ANALYZE_PRESETS)[number]["value"];

/** What the chip group can hold: a fixed window, or the custom value + unit row. */
export type AnalyzePreset = AnalyzePresetValue | "custom";

export const DEFAULT_ANALYZE_PRESET: AnalyzePreset = "15m";

/**
 * Bounds on a CUSTOM window. The agent accepts any window with `from < to`, so
 * the floor is ours: without it an upgraded customer could request less than the
 * 15 minutes a free customer is locked to, inverting the tiers. The ceiling is
 * the agent's own hard cap — retention runs to 30 days, but the agent refuses
 * to analyze more than 7.
 */
export const MIN_WINDOW_SECONDS = 15 * TIME_UNIT_SECONDS.minutes; // 900
export const MAX_WINDOW_SECONDS = 7 * TIME_UNIT_SECONDS.days; // 604800

/** The form's field state — the single shape `buildVariables`/`validate` read. */
export interface PerformanceAgentFormState {
  /** App or worker NAME (topology key) → `AGENT_APPLICATION`. */
  application: string;
  /** Which chip is selected. `"custom"` is the only one that reads the two below. */
  analyzePreset: AnalyzePreset;
  /** Raw integer input for a custom analysis window. Ignored for a preset. */
  analyzePastValue: string;
  analyzePastUnit: TimeUnit;
  /** Provider alias → `MODEL`. */
  provider: Provider;
}

/**
 * The analysis window in seconds — the ONE place a preset or the custom
 * value + unit pair becomes a number, shared by `validate` and
 * `buildVariables` so they can never disagree.
 *
 * `find` misses only for `"custom"`: `AnalyzePreset` is derived from
 * `ANALYZE_PRESETS`, so every other member has a row by construction.
 * Returns `NaN` for a malformed custom value — `validate` rejects it there.
 */
export const resolveWindowSeconds = (
  state: PerformanceAgentFormState
): number => {
  const preset = ANALYZE_PRESETS.find(
    option => option.value === state.analyzePreset
  );

  return preset
    ? preset.amount * TIME_UNIT_SECONDS[preset.unit]
    : Number(state.analyzePastValue) * TIME_UNIT_SECONDS[state.analyzePastUnit];
};

export const initialPerformanceAgentState = (
  application: string
): PerformanceAgentFormState => ({
  application,
  analyzePreset: DEFAULT_ANALYZE_PRESET,
  analyzePastValue: DEFAULT_ANALYZE_PAST_VALUE,
  analyzePastUnit: DEFAULT_ANALYZE_PAST_UNIT,
  provider: DEFAULT_PROVIDER
});
